'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useState, useEffect } from 'react';
import { RotateCw, CheckCircle2, MessageSquare, Star, PanelLeft } from 'lucide-react';
import Link from 'next/link';
import NotificationBell from './NotificationBell';

export default function TopHeaderBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  
      const isHideMenu = pathname.startsWith('/s/') || 
                      pathname.startsWith('/shared/') || 
                      pathname.includes('/view') || 
                      searchParams.get('shared') === 'true' || 
                      searchParams.get('hideMenu') === 'true' ||
                      searchParams.get('public') === 'true';

  if (pathname === '/login' || pathname === '/landing' || isHideMenu) {
    return null;
  }


  const handleSetDefaultPage = () => {
    if (typeof window !== 'undefined') {
      const current = window.location.pathname;
      const search = window.location.search;
      const fullPath = current + search;
      
      const title = prompt("กรุณาตั้งชื่อเมนูลัดสำหรับหน้านี้:", document.title || current);
      if (!title) return;
      
      let shortcuts = [];
      try {
        shortcuts = JSON.parse(localStorage.getItem('dashboard_shortcuts') || '[]');
      } catch(e) {}
      
      // Check if already exists
      const existing = shortcuts.findIndex((s: any) => s.path === fullPath);
      if (existing !== -1) {
        shortcuts[existing].title = title;
      } else {
        shortcuts.push({ path: fullPath, title, dateAdded: new Date().toISOString() });
      }
      
      localStorage.setItem('dashboard_shortcuts', JSON.stringify(shortcuts));
      alert(`บันทึกเมนูลัด "${title}" ไปยังแดชบอร์ดเรียบร้อยแล้ว!`);
    }
  };

  const handleSyncAll = async () => {
    if (syncing) return;
    setSyncing(true);
    setSynced(false);
    setProgressMsg('เริ่มดึงข้อมูล...');

    try {
      const activeDb = localStorage.getItem('active_company') || 'express_db';
      await fetch('/api/sync/all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ db: activeDb })
      });

      // Poll progress every 1s
      const timer = setInterval(async () => {
        try {
          const res = await fetch('/api/sync/progress').then(r => r.json());
          if (res.success) {
            if (res.is_syncing || res.request_sync) {
              if (res.total_files > 0) {
                setProgressMsg(`ดึงข้อมูล Express: ${res.current_file} (${res.processed_files}/${res.total_files})`);
              } else {
                setProgressMsg(res.current_file || 'กำลังซิงค์...');
              }
            } else {
              clearInterval(timer);
              setSyncing(false);
              setSynced(true);
              setProgressMsg('');
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('refresh_notifications'));
              }
              setTimeout(() => {
                setSynced(false);
                window.location.reload();
              }, 1200);
            }
          }
        } catch(e) {
          clearInterval(timer);
          setSyncing(false);
        }
      }, 1000);

    } catch (error) {
      console.error('Sync Error:', error);
      setSyncing(false);
    }
  };

  return (
    <div className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/60 pl-14 pr-3 md:px-8 py-2.5 flex items-center justify-between shrink-0 print:hidden">
      <div className="flex items-center gap-2.5">
        <button
          onClick={() => window.dispatchEvent(new Event("toggle-sidebar"))}
          className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-blue-500/50 text-slate-300 hover:text-blue-400 transition flex items-center gap-1 text-xs font-medium cursor-pointer shadow-sm"
          title="ย่อ / ขยาย เมนูด้านซ้าย"
        >
          <PanelLeft size={16} />
          <span className="hidden lg:inline text-[11px]">ย่อ/ขยายเมนู</span>
        </button>
        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] md:text-[11px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-sm shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
          <span className="hidden sm:inline">Express Cloud ERP Active</span>
          <span className="sm:hidden font-bold">Active</span>
        </span>

        <button
          onClick={handleSyncAll}
          disabled={syncing}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 hover:from-blue-500 hover:to-emerald-500 text-white shadow-md shadow-blue-900/30 transition active:scale-95 disabled:opacity-50 cursor-pointer border border-blue-400/30"
          title="ดึงข้อมูลสดล่าสุดทั้งหมดจากฐานข้อมูล Express"
        >
          <RotateCw size={14} className={syncing ? "animate-spin text-blue-200" : ""} />
          <span>{syncing ? (progressMsg || 'กำลังดึง...') : synced ? 'สำเร็จ!' : <><span className="hidden sm:inline">ซิงค์ทั้งหมด (Express)</span><span className="sm:hidden">ซิงค์</span></>}</span>
          {synced && <CheckCircle2 size={14} className="text-emerald-300 animate-bounce" />}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleSetDefaultPage}
          className="relative p-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-amber-500/50 text-slate-400 hover:text-amber-400 transition group flex items-center gap-1 text-xs font-bold shadow-md cursor-pointer"
          title="เพิ่มหน้านี้เป็นเมนูลัดบนแดชบอร์ด"
        >
          <Star size={16} className="group-hover:scale-110 transition text-amber-400" />
          <span className="hidden md:inline">ตั้งเป็นเมนูลัด</span>
        </button>
        <Link
          href="/chat"
          className="relative p-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-purple-500/50 text-slate-300 hover:text-purple-400 transition group flex items-center gap-1.5 text-xs font-bold shadow-md cursor-pointer"
          title="แชท & สื่อสารภายในทีม"
        >
          <MessageSquare size={18} className="group-hover:scale-110 transition transform text-purple-400" />
          <span className="hidden sm:inline">แชททีม</span>
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse border border-slate-900"></span>
        </Link>
        <NotificationBell />
      </div>
    </div>
  );
}
